import { Check, Minus } from '@digital-twins/design-system/icons'

import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'

export type ThreadStepGlyphState = 'done' | 'active' | 'pending' | 'unavailable'

interface ThreadStepGlyphProps {
  state: ThreadStepGlyphState
  /**
   * Prefix for the glyph's test id. The run stepper passes
   * `thread-run-activity`, which keeps its pinned ids
   * (`thread-run-activity-mark`, `-step-check`, `-step-pending`).
   */
  testIdPrefix: string
}

/**
 * The status mark one stepper row carries, shared by the run stepper
 * (`ThreadRunActivityIndicator`) and the activity checklist
 * (`ThreadActivitySteps`) so both read as one system
 * (docs/plans/thread-activity-steps.md, D10).
 *
 * `active` is the platform's own waiting glyph, which already honours
 * `prefers-reduced-motion`. `unavailable` is a quiet dash rather than a red
 * cross: the answer reports its own gaps, and the row only says the step did
 * not come back (D9).
 */
export function ThreadStepGlyph(props: ThreadStepGlyphProps) {
  const { state, testIdPrefix } = props

  if (state === 'active') {
    return (
      <TwinsMarkLoader
        className="h-4 w-4 text-muted-foreground"
        dataTestId={`${testIdPrefix}-mark`}
      />
    )
  }

  if (state === 'done') {
    return (
      <Check
        className="h-3.5 w-3.5 text-chart-positive"
        aria-hidden="true"
        data-testid={`${testIdPrefix}-step-check`}
      />
    )
  }

  if (state === 'unavailable') {
    return (
      <Minus
        className="h-3.5 w-3.5 text-muted-foreground"
        aria-hidden="true"
        data-testid={`${testIdPrefix}-step-unavailable`}
      />
    )
  }

  return (
    <span
      className="mt-1 h-2 w-2 rounded-full border border-muted-foreground/40"
      aria-hidden="true"
      data-testid={`${testIdPrefix}-step-pending`}
    />
  )
}
